import { useMutation } from "@apollo/client";
import { Button, FormLabel, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import { useLongPollingForJobWithFileOutput } from "hooks/useLongPollingForJobWithFileOutput";
import { useCallback, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

type GenerateDeliverablesDialogProps = {
  tenantId: string;
  open: boolean;
  onClose: () => void;
};

type FormValues = {
  allReportingYears: boolean;
  reportingYear?: number;
};

export function DownloadTierIIDeliverablesDialog({
  open,
  onClose,
  tenantId,
}: GenerateDeliverablesDialogProps) {
  const alerts = useAlerts();
  const [generateDeliverables, { loading: isGeneratingDeliverables }] =
    useMutation(
      gql(`
    mutation DownloadTierIIDeliverables($tenantId: ID!, $reportingYear: Int) {
      downloadTierIIDeliverables(tenantId: $tenantId, reportingYear: $reportingYear) {
        jobId
      }
    }
  `)
    );

  const defaultValues: FormValues = useMemo(() => {
    return {
      allReportingYears: true,
      reportingYear: currentTierIIReportingYear,
    };
  }, []);

  const form = useForm<FormValues>({ defaultValues });
  const allReportingYears = form.watch("allReportingYears");

  // Callbacks for the polling hook
  const handleUrlsReady = useCallback(
    (urls: string[]) => {
      for (const url of urls) {
        window.open(url, "_blank");
      }
      onClose();
    },
    [onClose]
  );

  const handleError = useCallback(
    (err: Error) => {
      alerts.error(err.message);
      onClose();
    },
    [alerts, onClose]
  );

  const { polling, startPolling } = useLongPollingForJobWithFileOutput({
    timeoutInSeconds: 1200,
    onUrlsReady: handleUrlsReady,
    onError: handleError,
  });

  const handleSubmit = async (formData: FormValues) => {
    const { data } = await generateDeliverables({
      variables: {
        tenantId,
        reportingYear: formData.allReportingYears
          ? undefined
          : formData.reportingYear,
      },
      onCompleted: (data) => {
        const jobId = data.downloadTierIIDeliverables.jobId;
        startPolling(jobId);
        alerts.success(
          "Deliverables job queued, this may take a few minutes, but please stay on this page."
        );
      },
      onError: (error) => {
        alerts.error(error.message);
        onClose();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="Download Deliverables"
      primaryActionButton={
        <Button
          onClick={form.handleSubmit(handleSubmit)}
          variant="contained"
          loading={isGeneratingDeliverables || polling}
        >
          Generate
        </Button>
      }
    >
      <FormProvider {...form}>
        <RadioGroupField
          label=""
          name="allReportingYears"
          radioOptions={[
            { label: "For all reporting years", value: true },
            { label: "For a specific reporting year", value: false },
          ]}
          hideErrors
        />
        {!allReportingYears && (
          <Controller
            name="reportingYear"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <Stack direction="row" alignItems="center" spacing={2}>
                <FormLabel>Please select a reporting year</FormLabel>
                <TierIIYearPicker
                  selectProps={{
                    size: "small",
                  }}
                  value={value}
                  onChange={onChange}
                />
              </Stack>
            )}
          />
        )}
      </FormProvider>
    </Dialog>
  );
}
